/**
 * Filing List Component
 *
 * Displays a table of filings with status badges and actions.
 *
 * Session 3.2: Filing History Frontend
 */

import { FilingStatusBadge } from './FilingStatusBadge';
import type { FilingSummary } from '@/types/filing';
import { formatCurrency, FORMAT_TYPE_LABELS } from '@/types/filing';

interface FilingListProps {
  /** List of filing summaries */
  filings: FilingSummary[];
  /** Callback when a filing row is clicked */
  onRowClick?: (filing: FilingSummary) => void;
  /** Callback when download is clicked */
  onDownload?: (filing: FilingSummary) => void;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * Filing list table component.
 *
 * Displays:
 * - Period and format type
 * - Employee count
 * - Total contributions
 * - Status badge
 * - Generated date
 * - Actions (View, Download)
 */
export function FilingList({
  filings,
  onRowClick,
  onDownload,
  isLoading = false,
}: FilingListProps) {
  if (isLoading) {
    return <FilingListSkeleton />;
  }

  if (filings.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No filings found
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Generate a C10 filing to see it here.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Period
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Employees
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Total Contributions
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Generated
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filings.map(filing => (
            <FilingRow
              key={filing.id}
              filing={filing}
              onRowClick={onRowClick}
              onDownload={onDownload}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface FilingRowProps {
  filing: FilingSummary;
  onRowClick?: (filing: FilingSummary) => void;
  onDownload?: (filing: FilingSummary) => void;
}

function FilingRow({ filing, onRowClick, onDownload }: FilingRowProps) {
  const generatedDate = new Date(filing.generated_at).toLocaleDateString(
    'en-US',
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }
  );

  const handleRowClick = () => {
    onRowClick?.(filing);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload?.(filing);
  };

  return (
    <tr
      className={`${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''} transition-colors`}
      onClick={handleRowClick}
    >
      {/* Period */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {filing.period_display}
        </div>
        <div className="text-sm text-gray-500">
          {FORMAT_TYPE_LABELS[filing.format_type]}
        </div>
      </td>

      {/* Employees */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">{filing.employee_count}</div>
      </td>

      {/* Total Contributions */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {formatCurrency(filing.total_contributions)}
        </div>
        <div className="text-xs text-gray-500">
          Wages: {formatCurrency(filing.total_wages)}
        </div>
      </td>

      {/* Status */}
      <td className="px-6 py-4 whitespace-nowrap">
        <FilingStatusBadge status={filing.status} size="sm" />
      </td>

      {/* Generated */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {generatedDate}
      </td>

      {/* Actions */}
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <button
          onClick={handleRowClick}
          className="text-blue-600 hover:text-blue-900 mr-4"
        >
          View
        </button>
        <button
          onClick={handleDownload}
          className="text-gray-600 hover:text-gray-900"
        >
          Download
        </button>
      </td>
    </tr>
  );
}

/**
 * Loading skeleton for filing list.
 */
function FilingListSkeleton() {
  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
      <div className="animate-pulse">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
          <div className="flex space-x-8">
            <div className="h-4 bg-gray-200 rounded w-16" />
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-4 bg-gray-200 rounded w-32" />
            <div className="h-4 bg-gray-200 rounded w-16" />
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-4 bg-gray-200 rounded w-16 ml-auto" />
          </div>
        </div>
        {/* Rows */}
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-8">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-24" />
                <div className="h-3 bg-gray-100 rounded w-20" />
              </div>
              <div className="h-4 bg-gray-200 rounded w-8" />
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-20" />
                <div className="h-3 bg-gray-100 rounded w-16" />
              </div>
              <div className="h-6 bg-gray-200 rounded-full w-20" />
              <div className="h-4 bg-gray-200 rounded w-24" />
              <div className="flex space-x-4 ml-auto">
                <div className="h-4 bg-gray-200 rounded w-10" />
                <div className="h-4 bg-gray-200 rounded w-16" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
